import type { Question } from '../../types';
import { HAB_MH_CONDITION, US_STATE_OPTIONS } from './shared-options';

// ---------------------------------------------------------------------------
// Workers Compensation — Multi-State Supplement
// Source: Centered Partners MHMF intake — "Workers Comp" sheet.
// Class code matrix: state × class code × payroll, plus X-Mod, audits,
// 1099 usage, return-to-work, and safety program.
// ---------------------------------------------------------------------------

const WC_CONDITION = [
  { field: 'coverage_lines', operator: 'contains' as const, value: 'workers_comp' },
  ...HAB_MH_CONDITION,
];

export const wcMultistateQuestions: Question[] = [
  {
    id: 'wc_class_matrix',
    text: 'Payroll by class code, by state',
    subtext:
      'One entry per state + class code combination. Projected payroll for the next 12 months.',
    section: 'employees',
    group: 'wc',
    sort_order: 500,
    field_type: 'repeating_group',
    required: true,
    field_path: 'wc.class_matrix',
    conditions: WC_CONDITION,
    repeating_group: {
      min_rows: 1,
      max_rows: 60,
      add_button_label: 'Add another state / class code',
      row_label: 'Class entry',
      fields: [
        {
          id: 'wc_m_state',
          text: 'State',
          section: 'employees',
          sort_order: 10,
          field_type: 'select',
          options: US_STATE_OPTIONS,
          required: true,
          field_path: 'state',
          conditions: [],
        },
        {
          id: 'wc_m_class_code',
          text: 'Class code',
          section: 'employees',
          sort_order: 20,
          field_type: 'select',
          options: [
            { label: '9015 — Apartment / MHC operations', value: '9015' },
            { label: '9082 — Clerical office', value: '9082' },
            { label: '8742 — Outside sales', value: '8742' },
            { label: '5478 — Mfg housing assembly / set-up', value: '5478' },
            { label: 'Other', value: 'other' },
          ],
          required: true,
          field_path: 'class_code',
          conditions: [],
        },
        {
          id: 'wc_m_class_other',
          text: 'Other class code',
          section: 'employees',
          sort_order: 30,
          field_type: 'text',
          placeholder: '9102',
          required: true,
          field_path: 'class_code_other',
          conditions: [{ field: 'class_code', operator: 'eq', value: 'other' }],
        },
        {
          id: 'wc_m_payroll',
          text: 'Projected annual payroll',
          section: 'employees',
          sort_order: 40,
          field_type: 'currency',
          placeholder: '250000',
          required: true,
          field_path: 'payroll',
          conditions: [],
        },
        {
          id: 'wc_m_ft',
          text: 'Full-time employees',
          section: 'employees',
          sort_order: 50,
          field_type: 'number',
          placeholder: '4',
          required: true,
          field_path: 'ft_count',
          conditions: [],
        },
        {
          id: 'wc_m_pt',
          text: 'Part-time / seasonal employees',
          section: 'employees',
          sort_order: 60,
          field_type: 'number',
          placeholder: '1',
          required: true,
          field_path: 'pt_seasonal_count',
          conditions: [],
        },
      ],
    },
    tags: ['wc', 'lob', 'repeating'],
  },
  {
    id: 'wc_xmod',
    text: 'Most recent experience modification factor (X-Mod)',
    subtext: 'From your NCCI or state bureau worksheet. Enter 1.0 if you don’t have one yet.',
    section: 'employees',
    group: 'wc',
    sort_order: 510,
    field_type: 'number',
    placeholder: '0.92',
    required: true,
    field_path: 'wc.xmod',
    conditions: WC_CONDITION,
    tags: ['wc', 'lob'],
  },
  {
    id: 'wc_onsite_pct',
    text: 'What percentage of staff work on-site (vs. office)?',
    section: 'employees',
    group: 'wc',
    sort_order: 520,
    field_type: 'percentage',
    placeholder: '70',
    required: true,
    validation: { min: 0, max: 100 },
    field_path: 'wc.onsite_pct',
    conditions: WC_CONDITION,
    tags: ['wc', 'lob'],
  },
  {
    id: 'wc_1099_use',
    text: 'Do you use 1099 contractors?',
    section: 'employees',
    group: 'wc',
    sort_order: 530,
    field_type: 'boolean',
    required: true,
    field_path: 'wc.contractor_1099_use',
    conditions: WC_CONDITION,
    tags: ['wc', 'lob'],
  },
  {
    id: 'wc_1099_detail',
    text: 'What work do 1099 contractors perform — and are contracts in place?',
    section: 'employees',
    group: 'wc',
    sort_order: 540,
    field_type: 'textarea',
    placeholder: 'Lawn care and snow removal; signed contracts with COIs on file.',
    required: true,
    field_path: 'wc.contractor_1099_detail',
    conditions: [...WC_CONDITION, { field: 'wc_1099_use', operator: 'eq', value: true }],
    tags: ['wc', 'lob'],
  },
  {
    id: 'wc_rtw_program',
    text: 'Is there a return-to-work / light duty program?',
    section: 'employees',
    group: 'wc',
    sort_order: 550,
    field_type: 'boolean',
    required: true,
    field_path: 'wc.rtw_program',
    conditions: WC_CONDITION,
    tags: ['wc', 'lob'],
  },
  {
    id: 'wc_safety_cadence',
    text: 'How often are safety meetings held?',
    section: 'employees',
    group: 'wc',
    sort_order: 560,
    field_type: 'select',
    options: [
      { label: 'Weekly', value: 'weekly' },
      { label: 'Monthly', value: 'monthly' },
      { label: 'Quarterly', value: 'quarterly' },
      { label: 'Ad hoc', value: 'ad_hoc' },
      { label: 'No formal program', value: 'none' },
    ],
    required: true,
    field_path: 'wc.safety_cadence',
    conditions: WC_CONDITION,
    tags: ['wc', 'lob'],
  },
  {
    id: 'wc_capex_planned',
    text: 'Major capex or construction expected in the next 12 months?',
    section: 'employees',
    group: 'wc',
    sort_order: 570,
    field_type: 'boolean',
    required: true,
    field_path: 'wc.capex_planned',
    conditions: WC_CONDITION,
    tags: ['wc', 'lob'],
  },
  {
    id: 'wc_capex_detail',
    text: 'Will that work be done by employees or contracted out?',
    section: 'employees',
    group: 'wc',
    sort_order: 580,
    field_type: 'textarea',
    placeholder: 'Clubhouse renovation — contracted to a GC with insurance requirements.',
    required: true,
    field_path: 'wc.capex_detail',
    conditions: [...WC_CONDITION, { field: 'wc_capex_planned', operator: 'eq', value: true }],
    tags: ['wc', 'lob'],
  },
];
